import { FileText, Paperclip, Plus, Trash2 } from "lucide-react";
import type {
  TaskAttachment,
  UserSummary,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { userLabel } from "@/modules/tasks/directories";
import type { TaskAttachmentsState } from "@/modules/tasks/components/useTaskAttachments";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** What the person is told, in the words the server or this screen chose. */
function rejectionText(rejection: WriteRejection): string {
  if (rejection.kind === "forbidden") {
    return "You do not have permission to change files on this task.";
  }
  return rejection.message;
}

/**
 * The Attachments card on a task: each file with who added it and when, a
 * delete for people who can edit, and "No files attached." when there are none
 * (FR-TSK-13, #625).
 */
export function AttachmentsCard({
  attachments,
  users,
  state,
  taskId,
}: {
  attachments: TaskAttachment[];
  users: UserSummary[];
  state: TaskAttachmentsState;
  taskId: string;
}) {
  const data = useDataSource();
  const { canEdit, rejection } = state;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">
          Attachments
          {attachments.length > 0 ? ` · ${String(attachments.length)}` : ""}
          {state.uploading ? " · uploading…" : ""}
        </CardTitle>
        {canEdit ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 sm:h-8"
            onClick={state.openPicker}
          >
            <Plus />
            Attach a file
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {state.picker}
        {attachments.length === 0 ? (
          <p className="text-muted-foreground text-sm">No files attached.</p>
        ) : (
          <ul className="space-y-2">
            {attachments.map((attachment) => {
              const url = data.tasks.attachmentUrl(taskId, attachment.id);
              return (
                <li
                  key={attachment.id}
                  className="flex items-center gap-3 text-sm"
                >
                  {attachment.contentType.startsWith("image/") && url ? (
                    // The same authenticated route a download uses: an
                    // `<img>` request ignores `Content-Disposition:
                    // attachment`, so this renders inline.
                    <img
                      src={url}
                      alt=""
                      className="size-10 shrink-0 rounded border object-cover"
                    />
                  ) : (
                    <span className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded border">
                      {attachment.contentType === "application/pdf" ? (
                        <FileText className="size-4" aria-hidden />
                      ) : (
                        <Paperclip className="size-4" aria-hidden />
                      )}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate font-medium underline-offset-2 hover:underline"
                    >
                      {attachment.filename}
                    </a>
                    <p className="text-muted-foreground text-xs">
                      {formatBytes(attachment.sizeBytes)} ·{" "}
                      {userLabel(users, attachment.uploadedByUserId)} ·{" "}
                      {formatWhen(attachment.uploadedAt)}
                    </p>
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 sm:size-9"
                      aria-label={`Delete ${attachment.filename}`}
                      disabled={state.deleting}
                      onClick={() => {
                        state.requestDelete(attachment);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {canEdit ? (
          <p className="text-muted-foreground text-xs">
            Images and PDFs up to 25 MB. Drop a file anywhere on this task, or
            paste a screenshot.
          </p>
        ) : null}
        {rejection ? (
          <p role="alert" className="text-destructive text-sm">
            {rejectionText(rejection)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
